import { Close as CloseIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormHelperText,
  InputAdornment,
  Stack,
  TextField,
} from "@mui/material";
import axios from "axios";
import { FormikProps } from "formik";
import { ChangeEvent, useRef, useState } from "react";
import { AddAssesmentGuidilineRequest } from "../../../redux/Assessment/assessmentApis";
import { useGenerateUploadLinkMutation } from "../../../redux/Documents/documentSlice";

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_FILE_COUNT = 5;

const ALLOWED_TYPES = [
  "image/png",
  "image/jpeg",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

type UploadingFile = {
  name: string;
  progress: number;
};

type Props = {
  formikProps: FormikProps<AddAssesmentGuidilineRequest>;
  name?: string;
};

const FormikInputFileUploadWithAzure = ({
  formikProps,
  name = "attachmentList",
}: Props) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string>("");
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);

  const filenames: string[] = formikProps.values[name] || [];

  const [getLink] = useGenerateUploadLinkMutation();

  const uploadSingleFile = async (file: File): Promise<string | null> => {
    const cleanFileName = file.name
      .replace(/ /g, "_")
      .replace(/[^a-zA-Z0-9._-]/g, "");

    setUploadingFiles((prev) => [
      ...prev,
      { name: cleanFileName, progress: 0 },
    ]);

    try {
      const response = await getLink({
        fileName: cleanFileName,
        id: "none",
      }).unwrap();

      const blobUrl = response.link;

      const extractFileNameList = blobUrl.split("?")[0].split("/");
      const extractedFileName =
        extractFileNameList[extractFileNameList.length - 1];

      await axios.put(blobUrl, file, {
        headers: {
          "x-ms-blob-type": "BlockBlob",
          "Content-Type": file.type,
        },
        onUploadProgress: (event) => {
          const percent = Math.round((event.loaded * 100) / (event.total || 1));
          setUploadingFiles((prev) =>
            prev.map((f) =>
              f.name === cleanFileName ? { ...f, progress: percent } : f,
            ),
          );
        },
      });

      return extractedFileName;
    } catch (err) {
      console.error(`Upload failed for ${file.name}`, err);
      setError(`Upload failed for ${file.name}`);
      return null;
    } finally {
      setUploadingFiles((prev) => prev.filter((f) => f.name !== cleanFileName));
    }
  };

  const validateFiles = (newFiles: File[]): File[] => {
    setError("");
    const valid: File[] = [];

    for (const file of newFiles) {
      const cleanName = file.name
        .replace(/ /g, "_")
        .replace(/[^a-zA-Z0-9._-]/g, "");

      if (
        filenames.length + uploadingFiles.length + valid.length >=
        MAX_FILE_COUNT
      ) {
        setError(`Maximum of ${MAX_FILE_COUNT} files allowed.`);
        break;
      }

      if (file.size > MAX_FILE_SIZE) {
        setError(`"${file.name}" exceeds 10MB limit.`);
        continue;
      }

      if (!ALLOWED_TYPES.includes(file.type)) {
        setError(`File type not allowed: "${file.name}"`);
        continue;
      }

      const isDuplicate =
        filenames.includes(cleanName) ||
        uploadingFiles.some((f) => f.name === cleanName);

      if (!isDuplicate) {
        valid.push(file);
      }
    }

    return valid;
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);
    const validFiles = validateFiles(selectedFiles);

    if (validFiles.length === 0) return;

    const results = await Promise.all(validFiles.map(uploadSingleFile));
    const successfulNames = results.filter((name): name is string => !!name);

    formikProps.setFieldValue(name, [...filenames, ...successfulNames]);

    event.target.value = ""; // reset input
  };

  const handleDeleteFile = (fileName: string) => {
    formikProps.setFieldValue(
      name,
      filenames.filter((f) => f !== fileName),
    );
  };

  return (
    <Box>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".png, .jpg, .jpeg, .pdf, .doc, .docx"
        style={{ display: "none" }}
        onChange={handleFileChange}
      />

      <TextField
        value=""
        placeholder="Click here to upload files"
        onClick={() => fileInputRef.current?.click()}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <Button
                variant="contained"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  fileInputRef.current?.click();
                }}
              >
                Add file
              </Button>
            </InputAdornment>
          ),
        }}
        fullWidth
        error={!!error}
        sx={{ cursor: "pointer" }}
      />

      {error && <FormHelperText error>{error}</FormHelperText>}

      <Stack direction="row" spacing={1} mt={2} flexWrap="wrap">
        {filenames.map((name) => (
          <Chip
            key={name}
            label={name}
            onDelete={() => handleDeleteFile(name)}
            deleteIcon={<CloseIcon fontSize="small" />}
            sx={{
              backgroundColor: "#f0f0f0",
              fontSize: 14,
              border: "none",
            }}
          />
        ))}

        {uploadingFiles.map((file) => (
          <Chip
            key={file.name}
            label={file.name}
            deleteIcon={
              <CircularProgress
                size={18}
                variant="determinate"
                value={file.progress}
                color="inherit"
              />
            }
            disabled
            sx={{
              backgroundColor: "#f0f0f0",
              fontSize: 13,
              border: "none",
              opacity: 0.6,
            }}
          />
        ))}
      </Stack>
    </Box>
  );
};

export default FormikInputFileUploadWithAzure;
